import { deepSignal } from 'deepsignal';
import { CHARITY } from '@util/const.js';
import {
  useDeepSignalFromLocalStorage,
  useSignalWithLocalStorage,
} from '@util/deepSignals.js';
import { useContext, useState } from 'preact/hooks';
import { ProductContext } from '../../context/productContext.js';
import { buildCustomizedURL, isYes } from '@util/common.js';
import CustomizeSection from '@components/overlays/shared/customize-section.jsx';
import Picker from '@components/input/picker.input.jsx';
import SimpleLinkSetup from '@components/overlays/social/link-setup/simple-link-setup.jsx';
import { tiltify } from '@playlive/tiltify-lib';
import { useQuery } from '@tanstack/react-query';
import useTiltifyConfig from '@hooks/useTiltifyConfig.js';
import ScaledOverlayWrapper from '@components/overlays/wrapper/scaled-overlay-wrapper.jsx';
import { FundraisingEventOverlay } from '@playlive/overlay-core/overlays/fundraising-event';
import LoadingSpinner from '@components/shared/loading-spinner.jsx';
import HorizontalCustomizeWrapper from '@components/overlays/wrapper/horizonal-customize-wrapper.jsx';

const config = deepSignal({
  charityType: CHARITY.TILTIFY,
  eventID: null,
});

const useBetaOverlays = isYes(import.meta.env.VITE_BETA_OVERLAYS || 'no');
const urlBase = useBetaOverlays ? 'overlays/event-total' : 'event-total';

export default function TotalOverlayPage() {
  useDeepSignalFromLocalStorage(config);
  const setConfigState = useSignalWithLocalStorage(config);
  const tiltifyConfig = useTiltifyConfig();
  const overlayInfo = useContext(ProductContext);
  const overlayURL = buildCustomizedURL({
    config: {
      ...tiltifyConfig,
      charityType: CHARITY.TILTIFY,
    },
    urlBase,
    urlSuffix: 'tiltify/' + config.eventID,
    addCharityType: false,
    urlSimplified: true,
  });

  const filterEvents = (event) => {
    if (event.status === 'published') {
      const eventPublishedAt = new Date(event.published_at);

      if (eventPublishedAt.getFullYear() === new Date().getFullYear() - 1) {
        return (
          eventPublishedAt.getMonth() === 12 ||
          eventPublishedAt.getMonth() === 11
        );
      }

      return (
        eventPublishedAt.getFullYear() === new Date().getFullYear() ||
        event.name.includes(`${new Date().getFullYear()}`)
      );
    }

    return false;
  };
  const sortEvents = (a, b) =>
    new Date(b.published_at) - new Date(a.published_at);
  const mapEvents = (event) => ({ name: event.name, value: event.id });

  const [eventDetails, setEventDetails] = useState({
    eventName: '',
    goalAmount: 0,
    currentAmount: 0,
  });
  const [events, setEvents] = useState([]);
  const eventOptions = events.map(mapEvents);
  const { data, isError, isFetched } = useQuery({
    queryKey: ['tiltify', 'getCurrentEvents'],
    queryFn: async () => {
      tiltify.SetPublicMode();
      tiltify.UseProxy();

      return tiltify
        .GetCurrentEvents()
        .then((res) => res['data'].filter(filterEvents).sort(sortEvents));
    },
  });

  function updateEventDetails(eventID) {
    const event = events.find((event) => event.id === eventID);

    if (event) {
      let eventName;
      let goalAmount = 0;
      let currentAmount = 0;

      eventName = event.name;

      if (Object.hasOwn(event, 'goal') && event.goal)
        goalAmount = parseFloat(event.goal.value);

      if (
        Object.hasOwn(event, 'total_amount_raised') &&
        event.total_amount_raised
      )
        currentAmount = parseFloat(event.total_amount_raised.value);

      setEventDetails({
        eventName,
        goalAmount,
        currentAmount,
      });
    }
  }

  function updateSelectedEvent(e) {
    setConfigState(e);
    updateEventDetails(e.target.value);
  }

  if (!isError && data) setEvents(data);

  if (config.eventID === null && events.length > 0)
    config.eventID = events[0].id;

  if (!eventDetails.eventName.length && config.eventID)
    updateEventDetails(config.eventID);

  return (
    <div className="w-full overflow-x-hidden">
      <div className="flex flex-col xl:flex-row! justify-center overflow-x-hidden">
        <div className="example_overlay overflow-x-hidden w-full min-h-72 relative">
          <LoadingSpinner show={config.eventID === null} />
          <ScaledOverlayWrapper
            allowScaling={false}
            innerClassName="h-[300px]"
            className={config.eventID === null ? 'opacity-0' : 'opacity-100'}
          >
            <FundraisingEventOverlay
              eventName={eventDetails.eventName}
              currentAmount={eventDetails.currentAmount}
              goalAmount={eventDetails.goalAmount}
              showOriginalGoal={false}
              originalGoalAmount={0}
            />
          </ScaledOverlayWrapper>
        </div>

        <HorizontalCustomizeWrapper>
          <CustomizeSection
            className="h-auto lg:h-[313px]"
            title="Configuration"
          >
            <Picker
              loading={!isFetched}
              items={eventOptions}
              fieldName="eventID"
              onChange={updateSelectedEvent}
              labelText="Event"
              currentValue={config.eventID}
              description="Choose the St. Jude fundraising event you want to track"
            />
          </CustomizeSection>
        </HorizontalCustomizeWrapper>
      </div>

      <SimpleLinkSetup
        obsDimensions={overlayInfo.obsDimensions}
        overlayName={overlayInfo.name}
        overlayURL={overlayURL}
      />
    </div>
  );
}
